import { useState, useEffect, useRef } from 'react';
import { api } from '../api';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function LivePage({ onNavigate }) {
  const [status, setStatus] = useState('idle'); // idle | requesting | recording | processing | done | error
  const [seconds, setSeconds] = useState(0);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingId, setMeetingId] = useState(null);
  const [error, setError] = useState('');
  const [audioSize, setAudioSize] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const timerRef         = useRef(null);
  const streamRef        = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const formatSize = bytes => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // ── Start recording ──────────────────────────────────────────────────────────
  const startRecording = async () => {
    setError('');
    setStatus('requesting');

    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        }
      });

      streamRef.current = stream;
      chunksRef.current = [];
      setAudioSize(0);

      // Pick best supported format
      const mimeType = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ].find(type => MediaRecorder.isTypeSupported(type)) || '';

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
          setAudioSize(prev => prev + e.data.size);
        }
      };

      mediaRecorder.onerror = (e) => {
        setError('Recording error: ' + e.error?.message || 'Unknown error');
        setStatus('error');
        stopTimer();
      };

      // Collect data every 5 seconds
      mediaRecorder.start(5000);
      setStatus('recording');
      setSeconds(0);
      startTimer();

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError(err.message);
      }
      setStatus('error');
    }
  };

  // ── Stop and analyse ─────────────────────────────────────────────────────────
  const stopAndAnalyse = () => {
    if (!mediaRecorderRef.current) return;

    stopTimer();
    setStatus('processing');

    // Stop the recorder — this triggers the final ondataavailable
    mediaRecorderRef.current.stop();

    // Stop microphone stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    // Wait a moment for final chunk then upload
    setTimeout(async () => {
      try {
        const chunks = chunksRef.current;
        if (!chunks.length) {
          throw new Error('No audio recorded. Make sure your microphone is working.');
        }

        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const blob = new Blob(chunks, { type: mimeType });

        if (blob.size < 1000) {
          throw new Error('Recording too short. Please record at least a few seconds of audio.');
        }

        // Convert blob to File for upload
        const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `recording.${ext}`, { type: mimeType });
        const title = meetingTitle.trim() || `Live Recording — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;

        // Upload to backend — same endpoint as file upload
        const result = await api.uploadAudio(file, title);
        setMeetingId(result.meeting_id);
        setStatus('done');

      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    }, 500);
  };

  // ── Cancel ───────────────────────────────────────────────────────────────────
  const cancel = () => {
    stopTimer();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    chunksRef.current = [];
    setStatus('idle');
    setSeconds(0);
    setError('');
    setAudioSize(0);
  };

  const reset = () => { cancel(); setMeetingTitle(''); setMeetingId(null); };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="page fade-in">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.75rem' }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Live Recording</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Record any meeting and get an instant AI summary</div>
        </div>
      </div>

      <div className="grid-2col">

        {/* ── Recorder ── */}
        <div style={{ background: 'var(--bg-2)', border: `1px solid ${status === 'recording' ? 'rgba(255,68,85,0.4)' : 'var(--border)'}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.3s' }}>
          <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: status === 'recording' ? 'var(--red)' : 'var(--text-2)' }}>
              {status === 'recording' ? '⦿ RECORDING' : status === 'processing' ? '⟳ PROCESSING' : status === 'done' ? '✓ DONE' : '⦿ Recorder'}
            </span>
            {status === 'recording' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="waveform-bar" style={{ animationDelay: `${i * 0.12}s` }} />
                ))}
              </div>
            )}
          </div>

          <div style={{ padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>

            {/* Timer */}
            <div style={{ fontFamily: 'var(--mono)', fontSize: 48, fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1, color: status === 'recording' ? 'var(--red)' : status === 'done' ? 'var(--accent)' : 'var(--text-3)' }}>
              {fmt(seconds)}
            </div>

            {/* Audio size indicator */}
            {status === 'recording' && audioSize > 0 && (
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>
                {formatSize(audioSize)} captured
              </div>
            )}

            {/* Meeting title input — show before recording */}
            {(status === 'idle' || status === 'error') && (
              <div style={{ width: '100%' }}>
                <label style={{ display: 'block', fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  Meeting Title (optional)
                </label>
                <input
                  type="text"
                  placeholder="Sprint Review, Client Call..."
                  value={meetingTitle}
                  onChange={e => setMeetingTitle(e.target.value)}
                  style={{ textAlign: 'center' }}
                />
              </div>
            )}

            {/* Record button */}
            {status === 'idle' && (
              <button onClick={startRecording} style={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'var(--red)', border: 'none', cursor: 'pointer',
                fontSize: 26, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', boxShadow: '0 4px 20px rgba(255,68,85,0.3)',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >●</button>
            )}

            {status === 'requesting' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🎤</div>
                <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Allow microphone access...</div>
              </div>
            )}

            {status === 'recording' && (
              <button onClick={stopAndAnalyse} style={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'var(--red)', border: 'none', cursor: 'pointer',
                fontSize: 22, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'recordPulse 1.5s ease-in-out infinite',
                boxShadow: '0 4px 20px rgba(255,68,85,0.4)',
              }}>■</button>
            )}

            {status === 'processing' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 12 }}>
                  <div className="processing-bar" style={{ width: 200 }}><div className="processing-bar-fill" /></div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 4 }}>Transcribing audio...</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>This takes 10-30 seconds</div>
              </div>
            )}

            {status === 'done' && (
              <div style={{ textAlign: 'center', width: '100%' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)', marginBottom: 4 }}>Recording analysed!</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: '1.25rem' }}>AI is extracting action items and decisions</div>
                <div style={{ display: 'flex', gap: '0.625rem', justifyContent: 'center' }}>
                  <button className="btn btn-primary" onClick={() => { onNavigate('meeting-detail', meetingId); }}>→ View Results</button>
                  <button className="btn btn-outline btn-sm" onClick={reset}>Record Again</button>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div style={{ textAlign: 'center', width: '100%' }}>
                <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,68,85,0.25)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', color: 'var(--red)', fontSize: 13 }}>
                  ⚠ {error}
                </div>
              </div>
            )}

            {/* Status text */}
            {status === 'idle' && (
              <div style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'center', maxWidth: 220, lineHeight: 1.6 }}>
                Click to start recording. Works with any meeting — Zoom, Teams, Meet, phone calls.
              </div>
            )}

            {status === 'recording' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%' }}>
                <div style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>
                  Click ■ when done to stop and analyse
                </div>
                <button className="btn btn-ghost btn-sm" onClick={cancel} style={{ color: 'var(--text-3)' }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Right column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* How it works */}
          <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>⚡ How it works</span>
            </div>
            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              {[
                { icon: '🎤', step: 'Click Record', desc: 'Allow microphone access when prompted' },
                { icon: '📞', step: 'Join your meeting', desc: 'Open Zoom, Meet, Teams — or take a phone call' },
                { icon: '🗣️', step: 'Talk normally', desc: 'We capture everything through your microphone' },
                { icon: '⚡', step: 'Click Stop', desc: 'AI transcribes and analyses instantly' },
              ].map(s => (
                <div key={s.step} style={{ display: 'flex', gap: '0.875rem', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 18, width: 32, flexShrink: 0, textAlign: 'center' }}>{s.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{s.step}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>💡 Tips for best results</span>
            </div>
            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {[
                'Use headphones so speaker audio is captured through the mic',
                'Speak clearly and avoid background noise',
                'Works best for meetings under 60 minutes',
                'Name the meeting before recording for better organisation',
              ].map((tip, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.625rem', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
                  <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}>→</span>
                  {tip}
                </div>
              ))}
            </div>
          </div>

          {/* Platform compat */}
          <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>✓ Works with any platform</span>
            </div>
            <div style={{ padding: '1rem 1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {['Zoom', 'Google Meet', 'Microsoft Teams', 'Phone calls', 'Webex', 'In-person meetings'].map(p => (
                <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 12, color: 'var(--text-2)' }}>
                  <span style={{ color: 'var(--accent)', fontSize: 10 }}>●</span> {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
